import apiClient from "services/apiClient";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  List,
  message,
  Row,
  Segmented,
  Switch,
} from "antd";
import { RightOutlined, SearchOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Groups from "./Groups";
import { useMyContext } from "Context/MyContextProvider";
import { useTemplates } from "hooks/api";
import CustomDrawer from "components/shared-components/CustomDrawer";
import "react-transliterate/dist/index.css";
import NewMobilePreview from "./newMobilePreview";
import CustomTextComponent from "./CustomTextComponent";
import TemplateComponent from "./TemplateComponent";
import { debounce } from "lodash";
import Flex from "components/shared-components/Flex";
import CommonNumberField from "./CampainComps/CommonNumberField";
import {
  GetBlockNumber,
  handleRemoveBlockedNumbers,
  normalizePhoneNumber,
  validateCampaignInputs,
  buildValidateCampaignPayload,
  getValidateCampaignResult,
  getApiErrorMessage,
  handleCampaignValidationFailure,
  handleRemoveInactiveGatewayNumbers,
} from "../CustomUtils.js/CampaignUtils";
import MotionError from "./CampainComps/MotionError";
import ModeLindex from "./CampainComps/ModeLindex";
import {
  HandleDynamicValues,
  HandleTemplateChange,
} from "../CustomUtils.js/TemplateUtils";
import {
  formatNumber,
  validateUserAndBalance,
  scrollToTop,
  conicColors,
} from "./CampainComps/OtherUtils";
import ConditionalRender from "../Utils/ConditionalRender";
import PermissionChecker from "../Utils/PermissionChecker";
import usePermissionGuard from "../Hooks/usePermissionGuard";
import { useCampaignProgress } from "../Hooks/useCampaignProgress";
import useExcelImport from "../Hooks/useExcelImport";
import useInternationalGateway from "../Hooks/useInternationalGateway";
import ExcelUploadSection from "./CampainComps/ExcelUploadSection";

const Campaign = ({ isReTarget = false }) => {
  const routeLocation = useLocation();
  const {
    id: reTargetCampaignId,
    campaign_name: reTargetCampaignName,
    send_config,
    recipient_mode,
    report_summary: retargetReportSummary,
  } = routeLocation.state || {};
  const {
    UserData,
    api,
    authToken,
    navigate,
    buttonStyle,
    calculateRequredBalance,
    isMobile,
  } = useMyContext();

  const { templates = [] } = useTemplates();

  usePermissionGuard("View Campaign");

  const [form] = Form.useForm();

  const [templateList, setTemplateList] = useState([]);
  const [componentSize, setComponentSize] = useState("small");
  const [number, setNumber] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [, setNumberError] = useState(false);
  const [requestType] = useState("template");
  const [inputMode, setInputMode] = useState("manual"); // 'manual' | 'excel'
  const isExcelMode = inputMode === "excel";
  const [dynamicValues, setDynamicValues] = useState([]);
  const [dynamicButtonUrl, setDynamicUrlButton] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [newMesaage, setNewMessage] = useState("");
  const [name, setName] = useState("");
  const [templatePreview, setTemplatePreview] = useState([]);
  const [mediaLink, setMediaLink] = useState("");
  const [mediaName, setMediaName] = useState("");
  const [location, setLocation] = useState({
    latitude: "",
    longitude: "",
    name: "",
    address: "",
  });
  const [thumbnail] = useState("");
  const [mediaType] = useState("Media");
  const [isPreviewDrawerOpen, setIsPreviewDrawerOpen] = useState(false);
  const [isGroupsDrawerOpen, setIsGroupsDrawerOpen] = useState(false);
  const [template, setTemplate] = useState({
    header: null,
    body: null,
    footer: null,
    buttons: null,
  });
  const [, setTemplateKey] = useState(0);
  const [templateInputs, setTemplateInputs] = useState([]);
  const [templateUrlInputs, setTemplateUrlInputs] = useState([]);
  const [urlButtons] = useState([]);
  // Numbers selected from Excel import / status checkboxes (additional recipients)
  const [numbers, SetNumbers] = useState([]);
  // Numbers selected from Contact Groups (separate textarea)
  const [groupNumbers, setGroupNumbers] = useState([]);
  const [groupSelection, setGroupSelection] = useState({ groupsCount: 0, contactsCount: 0 });

  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [templateCategory, setTemplateCategory] = useState("MARKETING");
  const [blockNumbers, setBlockNumbers] = useState([]);
  const [templateLanguage, setTemplateLanguage] = useState("");
  const [openMediaModel, setOpenMediaModel] = useState(false);
  const [openScheduleModel, setOpenSchedule] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customLanguage, setCustomLanguage] = useState("en");
  const [isInternational, setIsInternational] = useState(recipient_mode === 'international');
  const { gatewayEnabled } = useInternationalGateway({
    api,
    authToken,
    userId: UserData?.id,
  });

  useEffect(() => {
    if (!gatewayEnabled && isInternational) {
      setIsInternational(false);
    }
  }, [gatewayEnabled, isInternational]);

  // Retarget-specific state
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [reTargetReady, setReTargetReady] = useState(false);
  const statusCounts = {
    pending: Number(retargetReportSummary?.pending || 0),
    sent: Number(retargetReportSummary?.sent || 0),
    delivered: Number(retargetReportSummary?.delivered || 0),
    read: Number(retargetReportSummary?.read || 0),
    failed: Number(retargetReportSummary?.failed || 0),
  };

  useEffect(() => {
    if (!UserData?.id) return;
    GetBlockNumber(api, UserData, authToken, formatNumber, setBlockNumbers);
  }, [UserData, api, authToken]);

  useEffect(() => {
    if (templates.length > 0) {
      const nonCarouselTemplates = templates.filter(
        (template) =>
          !template.components?.some((comp) => comp.type === "CAROUSEL")
      );
      setTemplateList(nonCarouselTemplates);
    }
  }, [templates]);

  // Autofill from send_config for retarget mode
  useEffect(() => {
    if (!isReTarget || !send_config || templates.length === 0 || reTargetReady) return;

    // Prefill campaign name
    if (reTargetCampaignName) {
      const defaultName = reTargetCampaignName + ' - Retarget';
      setName(defaultName);
      form.setFieldsValue({ 'Campaign-Name': defaultName });
    }

    // Set template category
    setTemplateCategory(send_config.template_category);
    form.setFieldsValue({
      Template_category: send_config.template_category,
      Templates: send_config.template_name,
    });

    // Load template with initial values for retarget
    HandleTemplateChange(
      send_config.template_name,
      setMediaLink,
      setTemplateName,
      setTemplateKey,
      templates,
      setTemplateCategory,
      setTemplateLanguage,
      setTemplatePreview,
      setTemplate,
      setTemplateInputs,
      setTemplateUrlInputs,
      setDynamicValues,
      setDynamicUrlButton,
      send_config.body_values,    // initialBodyValues
      send_config.button_value    // initialButtonValues
    );

    // Override header media if present
    if (send_config.header?.media_id) {
      setMediaLink(send_config.header.media_id);
    }

    if (send_config.template_category === "LOCATION") {
      setLocation({
        latitude: send_config.location?.latitude || "",
        longitude: send_config.location?.longitude || "",
        name: send_config.location?.name || "",
        address: send_config.location?.address || "",
      });
    }

    setReTargetReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReTarget, send_config, templates, reTargetReady]);

  const retargetStatuses = [
    { label: 'Pending', value: 'pending' },
    { label: 'Sent', value: 'sent' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Read', value: 'read' },
    { label: 'Failed', value: 'failed' },
  ];

  const selectedStatusCount = selectedStatuses.reduce(
    (sum, status) => sum + (statusCounts[status] || 0),
    0
  );

  const handleStatusCheckboxChange = (checkedValues) => {
    setSelectedStatuses(checkedValues);
  };

  const hasRetargetRecipients = isReTarget && selectedStatuses.length > 0 && selectedStatusCount > 0;

  const withRetargetFields = (payload) => (
    hasRetargetRecipients
      ? {
          ...payload,
          source_campaign_id: reTargetCampaignId,
          statuses: selectedStatuses,
        }
      : payload
  );

  const onFormLayoutChange = ({ size }) => {
    setComponentSize(size);
  };

  useEffect(() => {
    if (number) {
      setNumberError(false);
    }
  }, [number]);

  const HandleTemplate = (value) => {
    ResetState();
    HandleTemplateChange(
      value,
      setMediaLink,
      setTemplateName,
      setTemplateKey,
      templates,
      setTemplateCategory,
      setTemplateLanguage,
      setTemplatePreview,
      setTemplate,
      setTemplateInputs,
      setTemplateUrlInputs,
      setDynamicValues,
      setDynamicUrlButton,
      dynamicValues
    );
  };

  const ResetState = () => {
    // Reset all states
    setMediaLink("");
    setTemplateName("");
    setTemplateKey((prevKey) => prevKey + 1);
    setTemplateCategory("");
    setTemplateLanguage("");
    setTemplatePreview(null);
    // setTemplate({ header: null, body: null, footer: null, buttons: null });
    setTemplateInputs([]);
    setTemplateUrlInputs([]);
    setDynamicValues([]);
    setDynamicUrlButton([]);
  };

  useEffect(() => {
    HandleDynamicValues(templatePreview, dynamicValues, setNewMessage);
  }, [templatePreview, dynamicValues]);

  useEffect(() => {
    if (requestType === "template") {
      setCustomMessage("");
    } else {
      setTemplatePreview([]);
      setTemplateInputs([]);
      setDynamicValues([]);
      setTemplateUrlInputs([]);
      setDynamicUrlButton([]);
      setTemplate({
        header: null,
        body: null,
        footer: null,
        buttons: null,
      });
    }
  }, [requestType]);

  const GetGroupNumber = useCallback((value, selectedGroups = 0, totalContacts = 0) => {
    const uniqueNumbersSet = [...new Set(value)];
    // Group selections should populate the `Mobile Number from Groups` field only
    setGroupNumbers(uniqueNumbersSet);
    setGroupSelection({ groupsCount: selectedGroups, contactsCount: totalContacts });
  }, []);

  const showModal = async () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };

  const showMediaModel = async () => {
    setOpenMediaModel(true);
  };
  const handleCancelMediaModel = () => {
    setOpenMediaModel(false);
  };

  const showScheduleModel = async () => {
    const filteredNumbers = filterNumbers([...numbers, ...groupNumbers], blockNumbers);
    const estimateCount = filteredNumbers.length + (hasRetargetRecipients ? selectedStatusCount : 0);
    const isEnoughBalance = await validateUserAndBalance(
      estimateCount,
      calculateRequredBalance,
      templateCategory,
      showModal
    );
    if (!isEnoughBalance) {
      showModal();
    } else {
      setOpenSchedule(true);
    }
  };
  const handleCancelScheduleModel = () => {
    setOpenSchedule(false);
  };

  useEffect(() => {
    const filteredList = templates
      .filter((item) => !templateCategory || item.category === templateCategory)
      .filter(
        (item) => !item.components?.some((comp) => comp.type === "CAROUSEL")
      );

    setTemplateList(filteredList);
  }, [templateCategory, templates]);

  const HandleUploadImage = async (path, name, id) => {
    if (path) {
      setMediaLink(path);
      setMediaName(name);
      setOpenMediaModel(false);
    }
  };

  const HandleScheduleTime = (value) => {
    const { $d } = value;
    const year = $d.getFullYear();
    const month = $d.getMonth() + 1;
    const day = $d.getDate();
    const hours = $d.getHours();
    const minutes = $d.getMinutes();
    // const seconds = $d.getSeconds();
    setScheduleDate(`${year}-${month}-${day}`);
    setScheduleTime(`${hours}:${minutes}:00`);
  };

  const filterNumbers = (numbers, blockNumbers) => {
    // Parse numbers from input field
    const numbersArray = number
      .split(",")
      .map((item) => item.trim())
      .flatMap((item) => item.split("\n"))
      .map((number) => number.trim());

    // Combine all numbers
    const combinedNumbers = [...numbers, ...numbersArray];

    // Normalize and filter valid numbers
    const normalizedNumbers = combinedNumbers
      .map((value) => normalizePhoneNumber(value, isInternational))
      .filter(Boolean);

    const blockedSet = new Set(
      (blockNumbers || [])
        .map((value) => normalizePhoneNumber(value, isInternational))
        .filter(Boolean)
    );

    // Deduplicate after normalization
    const uniqueNumbers = Array.from(new Set(normalizedNumbers));

    // Remove blocked numbers
    const filteredNumbers = uniqueNumbers.filter(
      (finalNumber) => !blockedSet.has(finalNumber)
    );

    return filteredNumbers;
  };

  // final submit
  const HandleSchedule = async () => {
    if (!name || !String(name).trim()) {
      setError("Please enter campaign name!");
      scrollToTop();
      return;
    }

    if (!scheduleDate || !scheduleTime) {
      setError("Please select a schedule date and time.");
      scrollToTop();
      return;
    }

    const filteredNumbers = filterNumbers([...numbers, ...groupNumbers], blockNumbers);

    if ((!Array.isArray(filteredNumbers) || filteredNumbers.length === 0) && !hasRetargetRecipients) {
      setError("No valid numbers to process");
      scrollToTop();
      return;
    }

    try {
      setLoading(true);
      setError("");

      const validationResponse = await apiClient.post(
        `${api}validate-campaign`,
        buildValidateCampaignPayload({
          userId: UserData.id,
          campaignType: requestType,
          templateName,
          customText: customMessage,
          filteredNumbers,
          templateCategory,
          isInternational: gatewayEnabled ? isInternational : false,
          sourceCampaignId: hasRetargetRecipients ? reTargetCampaignId : undefined,
          statuses: hasRetargetRecipients ? selectedStatuses : undefined,
        }),
        {
          headers: {
            Authorization: "Bearer " + authToken,
          },
        }
      );

      const validation = getValidateCampaignResult(validationResponse);

      if (validation.canProceed === false) {
        handleCampaignValidationFailure(validation, {
          setError,
          showBalanceModal: showModal,
          setInactiveGatewayModal,
          setInactiveGatewayGroups,
          setInactiveGatewayMessage,
          scrollToTop,
        });
        return;
      }

      const campaignPayload = withRetargetFields({
        name,
        user_id: UserData.id,
        numbers: isExcelMode ? importedData : filteredNumbers,
        campaign_type: requestType,
        custom_text: customMessage,
        custom_type: customType,
        template_name: templateName,
        template_language: templateLanguage,
        template_category: templateCategory,
        header_type: template?.header?.format,
        header_media_url: mediaLink,
        body_values: dynamicValues,
        button_type: template?.buttons?.buttons?.[0]?.type,
        button_value: dynamicButtonUrl,
        location: template?.header?.format === "LOCATION" ? location : undefined,
        excel_data: isExcelMode ? importedData : null,
        campaign_source: inputMode,
        international: gatewayEnabled ? isInternational : false,
        schedule_date: scheduleDate,
        schedule_time: scheduleTime,
      });

      const response = await apiClient.post(
        `${api}send-campaign`,
        campaignPayload,
        {
          headers: {
            Authorization: "Bearer " + authToken,
          },
        }
      );

      const scheduleTimeLabel =
        response?.data?.data?.schedule_time ||
        `${scheduleDate} ${scheduleTime}`;

      message.success(`Campaign scheduled for ${scheduleTimeLabel}`);
      setOpenSchedule(false);
      navigate("/schedule-campaign");
    } catch (err) {
      const validation = getValidateCampaignResult(err?.response);
      if (
        !handleCampaignValidationFailure(validation, {
          setError,
          showBalanceModal: showModal,
          setInactiveGatewayModal,
          setInactiveGatewayGroups,
          setInactiveGatewayMessage,
          scrollToTop,
        })
      ) {
        setError(getApiErrorMessage(err));
        scrollToTop();
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    progress,
    setProgress,
    setIsSending,
    isModalVisible,
    setIsModalVisible,
    startCampaignChannel,
    resetCampaign,
  } = useCampaignProgress();

  // Excel import hook
  const {
    importedData,
    fileList,
    disableUpload,
    uploadError,
    uploadProps,
    resetExcel,
    downloadSampleExcel,
    setImportedData,
  } = useExcelImport({ templateInputs, SetNumbers });

  // Reset Excel file whenever template changes (column count changes)
  useEffect(() => {
    resetExcel();
  }, [template, resetExcel]);

  // Navigation and refresh protection logic moved to useCampaignProgress hook

  const groupsRef = useRef(null);
  const [groupsKey, setGroupsKey] = useState(0);

  const styles = {
    textarea: {
      width: "100%",
      border: "1px solid #ced4da",
      borderRadius: ".5rem",
      outline: "none",
    },
  };

  const [customType, setCustomType] = useState("text");
  const [customMediaName, setMediaCustomName] = useState("text");
  const handleCustomMedia = (id, name) => {
    setCustomMessage(id);
    setMediaCustomName(name);
    setOpenMediaModel(false);
  };
  useEffect(() => {
    setCustomMessage("");
    setMediaCustomName("");
  }, [customType]);

  useEffect(() => {
    const debounced = debounce(() => {
      if (number || numbers || groupNumbers) {
        const inputNumbers = number
          ? number
            .split(",")
            .map((item) => item.trim())
            .flatMap((item) =>
              item.split("\n").map((subItem) => subItem.trim())
            )
            .filter(
              (item) =>
                item !== "" && (item.length === 10 || item.length === 12)
            )
          : [];

        const total = (numbers?.length || 0) + (groupNumbers?.length || 0) + inputNumbers.length;

        if (total > 10000) {
          setError("You can only process up to 10000 numbers.");
        } else {
          setError("");
        }
      }
    }, 500);

    debounced();
    return () => {
      debounced.cancel();
    };
  }, [number, numbers, groupNumbers, setError]);

  // Input validation function

  const [blockedNumbersModal, setBlockedNumbersModal] = useState(false);
  const [blockedNumbersSample, setBlockedNumbersSample] = useState([]);
  const [inactiveGatewayModal, setInactiveGatewayModal] = useState(false);
  const [inactiveGatewayGroups, setInactiveGatewayGroups] = useState([]);
  const [inactiveGatewayMessage, setInactiveGatewayMessage] = useState("");
  const [internationalInfoOpen, setInternationalInfoOpen] = useState(false);

  const handleInternationalModeChange = (checked) => {
    setIsInternational(checked);
    if (checked) {
      setInternationalInfoOpen(true);
    }
  };

  // Campaign channel logic moved to useCampaignProgress hook

  const submitCampaignToBackend = async (filteredNumbers) => {
    if (!name || !String(name).trim()) {
      setError("Please enter campaign name!");
      scrollToTop();
      return;
    }

    if ((!Array.isArray(filteredNumbers) || filteredNumbers.length === 0) && !hasRetargetRecipients) {
      setError("No valid numbers to process");
      scrollToTop();
      return;
    }

    try {
      setLoading(true);
      setError("");

      const validationResponse = await apiClient.post(
        `${api}validate-campaign`,
        buildValidateCampaignPayload({
          userId: UserData.id,
          campaignType: requestType,
          templateName,
          customText: customMessage,
          filteredNumbers,
          templateCategory,
          isInternational: gatewayEnabled ? isInternational : false,
          sourceCampaignId: hasRetargetRecipients ? reTargetCampaignId : undefined,
          statuses: hasRetargetRecipients ? selectedStatuses : undefined,
        }),
        {
          headers: {
            Authorization: "Bearer " + authToken,
          },
        }
      );

      const validation = getValidateCampaignResult(validationResponse);

      if (validation.canProceed === false) {
        handleCampaignValidationFailure(validation, {
          setError,
          showBalanceModal: showModal,
          setInactiveGatewayModal,
          setInactiveGatewayGroups,
          setInactiveGatewayMessage,
          scrollToTop,
        });
        return;
      }

      setIsModalVisible(true);

      const campaignPayload = withRetargetFields({
        name,
        user_id: UserData.id,
        numbers: isExcelMode ? importedData : filteredNumbers,
        campaign_type: requestType,
        custom_text: customMessage,
        custom_type: customType,
        template_name: templateName,
        template_language: templateLanguage,
        template_category: templateCategory,
        header_type: template?.header?.format,
        header_media_url: mediaLink,
        body_values: dynamicValues,
        button_type: template?.buttons?.buttons?.[0]?.type,
        button_value: dynamicButtonUrl,
        location: template?.header?.format === "LOCATION" ? location : undefined,
        excel_data: isExcelMode ? importedData : null,
        campaign_source: inputMode,
        international: gatewayEnabled ? isInternational : false,
      });

      const response = await apiClient.post(
        `${api}send-campaign`,
        campaignPayload,
        {
          headers: {
            Authorization: "Bearer " + authToken,
          },
        }
      );

      const newCampaignId =
        response?.data?.campaign_id ||
        response?.data?.data?.campaign_id ||
        response?.data?.campaign?.id;

      if (!newCampaignId) {
        setIsModalVisible(false);
        setError("Campaign queued but campaign_id was not returned");
        return;
      }

      setProgress(0);
      setIsSending(true);
      console.log(
        "Campaign process starting at " +
        new Date().toLocaleTimeString() +
        " for campaign ID: " +
        newCampaignId
      );

      if (filteredNumbers.length > 5) {
        startCampaignChannel(UserData?.id, newCampaignId);
      } else {
        console.log(
          "Small campaign detected (<= 5 numbers). Skipping WebSocket connection."
        );
        setProgress(100);
        setIsSending(false);
      }
    } catch (err) {
      setIsModalVisible(false);
      const validation = getValidateCampaignResult(err?.response);
      if (
        !handleCampaignValidationFailure(validation, {
          setError,
          showBalanceModal: showModal,
          setInactiveGatewayModal,
          setInactiveGatewayGroups,
          setInactiveGatewayMessage,
          scrollToTop,
        })
      ) {
        setError(getApiErrorMessage(err));
        scrollToTop();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseInactiveGatewayModal = () => {
    setInactiveGatewayModal(false);
  };

  const handleRemoveInactiveNumbers = () => {
    handleRemoveInactiveGatewayNumbers({
      inactiveGatewayGroups,
      numbers,
      groupNumbers,
      number,
      SetNumbers,
      setGroupNumbers,
      setNumber,
      importedData,
      setImportedData,
      international: isInternational,
      onClose: () => {
        setInactiveGatewayModal(false);
        setInactiveGatewayGroups([]);
        setInactiveGatewayMessage("");
        setError("");
      },
    });
  };

  const handleCloseBlockedModal = async () => {
    setBlockedNumbersModal(false);
    const filteredNumbers = filterNumbers([...numbers, ...groupNumbers], blockNumbers);
    await submitCampaignToBackend(filteredNumbers);
  };

  const HandleSubmitCampaign = async () => {
    // Guard: Excel mode requires a loaded file
    if (isExcelMode && (!importedData || importedData.length === 0)) {
      setError("Please upload and load an Excel file first");
      scrollToTop();
      return;
    }

    const validationResult = validateCampaignInputs({
      requestType,
      templateName,
      template,
      mediaLink,
      location,
      setError,
      scrollToTop,
      numbers: [...numbers, ...groupNumbers],
      number,
      blockNumbers,
      setBlockedNumbersSample,
      setBlockedNumbersModal,
      // In Excel mode, variable values come from the file — skip the
      // 'please fill in template variable fields' check by passing []
      templateInputs: isExcelMode ? [] : templateInputs,
      dynamicValues,
      templateUrlInputs,
      dynamicButtonUrl,
      international: isInternational,
      allowEmptyNumbers: hasRetargetRecipients,
    });

    if (!validationResult) return;

    const { filteredNumbers } = validationResult;
    await submitCampaignToBackend(filteredNumbers);
  };
  const RemoveBlockNumbers = () => {
    handleRemoveBlockedNumbers(
      [...numbers, ...groupNumbers],
      number,
      blockNumbers,
      SetNumbers,
      setNumber,
      setBlockedNumbersModal,
      isInternational
    );
  };

  const listData = [
    "Select your messaging category and template",
    "System will generate Alert for number of columns required in Excel",
    "The first column should always have mobile numbers",
    "Create Excel file with data only (i.e. without headers)",
    "The number of columns in excel other than mobile number should match the total number of variables in template",
    "Message on Blocked Mobile Numbers will not be sent",
    "Avoid using Special Characters in the variable contents",
    "Sample message preview for first row of excel will be available after submitting file",
  ];

  return (
    <>
      <ModeLindex
        open={open}
        handleCancel={handleCancel}
        UserData={UserData}
        openScheduleModel={openScheduleModel}
        handleCancelScheduleModel={handleCancelScheduleModel}
        HandleScheduleTime={HandleScheduleTime}
        HandleSchedule={HandleSchedule}
        scheduleDate={scheduleDate}
        scheduleTime={scheduleTime}
        openMediaModel={openMediaModel}
        handleCancelMediaModel={handleCancelMediaModel}
        HandleUploadImage={HandleUploadImage}
        requestType={requestType}
        handleCustomMedia={handleCustomMedia}
        progress={progress}
        isModalVisible={isModalVisible}
        loading={loading}
        conicColors={conicColors}
        resetCampaign={() => {
          resetCampaign();
          setNumber("");
          setGroupsKey((prevKey) => prevKey + 1);
          resetExcel();
          SetNumbers([]);
          setGroupNumbers([]);
        }}
        navigate={navigate}
        blockedNumbersModal={blockedNumbersModal}
        blockedNumbersSample={blockedNumbersSample}
        handleRemoveBlockedNumbers={RemoveBlockNumbers}
        handleCloseBlockedModal={handleCloseBlockedModal}
        allNumbers={[...numbers, ...groupNumbers]}
        blockNumbers={blockNumbers}
        number={number}
        inactiveGatewayModal={inactiveGatewayModal}
        inactiveGatewayMessage={inactiveGatewayMessage}
        inactiveGatewayGroups={inactiveGatewayGroups}
        handleRemoveInactiveNumbers={handleRemoveInactiveNumbers}
        handleCloseInactiveGatewayModal={handleCloseInactiveGatewayModal}
        internationalInfoOpen={internationalInfoOpen}
        handleCloseInternationalInfo={() => setInternationalInfoOpen(false)}
      />

      <Row gutter={12}>
        <PermissionChecker permission="View Campaign">
          <Col xs={24} sm={24} md={24} lg={12}>
            <Card
              title={isReTarget ? "Re-Target Campaign" : "Standard Campaign"}
              extra={
                gatewayEnabled ? (
                  <span className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span className="text-muted">Domestic</span>
                    <Switch
                      disabled={isReTarget}
                      checked={isInternational}
                      onChange={handleInternationalModeChange}
                      checkedChildren="INT"
                      unCheckedChildren="IN"
                    />
                    <span className="text-muted">International</span>
                  </span>
                ) : null
              }
            >
              <MotionError error={error} />
              <Col span={24}>
                <Form
                  form={form}
                  name="basicInformation"
                  layout="vertical"
                  initialValues={{ size: componentSize }}
                  onValuesChange={onFormLayoutChange}
                  size={componentSize}
                >
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        className="m-0"
                        label="Campaign Name"
                        name="Campaign-Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter campaign name!",
                          },
                        ]}
                      >
                        <Input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </Form.Item>

                      {/* Retarget: Status Category Checkboxes */}
                      <ConditionalRender condition={isReTarget}>
                        <Form.Item label="Select Recipients by Status">
                          <Checkbox.Group
                            className="d-flex flex-wrap gap-2"
                            onChange={handleStatusCheckboxChange}
                          >
                            {retargetStatuses.map((s) => (
                              <Checkbox key={s.value} value={s.value}>
                                {s.label}{" "}
                                <Badge
                                  count={statusCounts[s.value] || 0}
                                  overflowCount={999999}
                                  style={{
                                    backgroundColor: s.value === 'failed' ? '#ff4d4f' : '#52c41a',
                                    marginLeft: 4,
                                  }}
                                />
                              </Checkbox>
                            ))}
                          </Checkbox.Group>
                        </Form.Item>
                      </ConditionalRender>



                      {/* Template / Custom type selection — always first */}
                      <ConditionalRender condition={requestType === "Custom"}>
                        <CustomTextComponent
                          customLanguage={customLanguage}
                          setCustomLanguage={setCustomLanguage}
                          customMessage={customMessage}
                          buttonStyle={buttonStyle}
                          customMediaName={customMediaName}
                          customType={customType}
                          setCustomType={setCustomType}
                          showMediaModel={showMediaModel}
                          setCustomMessage={setCustomMessage}
                          styles={styles}
                        />
                      </ConditionalRender>
                      <ConditionalRender condition={requestType === "template"}>
                        <TemplateComponent
                          showTemplateType={false}
                          templateCategory={templateCategory}
                          setTemplateCategory={setTemplateCategory}
                          templateList={templateList}
                          HandleTemplate={HandleTemplate}
                          template={template}
                          templateName={templateName}
                          mediaType={mediaType}
                          showMediaModel={showMediaModel}
                          mediaLink={mediaLink}
                          buttonStyle={buttonStyle}
                          mediaName={mediaName}
                          setMediaLink={setMediaLink}
                          setLocation={setLocation}
                          location={location}
                          templateInputs={templateInputs}
                          templateUrlInputs={templateUrlInputs}
                          HandleSubmitCampaign={HandleSubmitCampaign}
                          showScheduleModel={showScheduleModel}
                          loading={loading}
                          hideBodyInputs={isExcelMode}
                          module="campaign"
                        />
                      </ConditionalRender>
                      {/* Input mode toggle */}
                      <Row>
                        <Col span={8}>
                          <Form.Item label="Input Mode" className="m-0 mb-2">
                            <Segmented
                              options={[
                                { label: "Manual Entry", value: "manual" },
                                { label: "Excel Upload", value: "excel" },
                              ]}
                              value={inputMode}
                              onChange={(val) => setInputMode(val)}
                            />
                          </Form.Item>
                        </Col>
                        {/* Number source — below template so templateName is set first */}
                        <ConditionalRender condition={!isExcelMode}>
                          <Col md={24}>
                            <CommonNumberField
                              numbers={groupNumbers}
                              additionalNumbers={numbers}
                              number={number}
                              setNumber={setNumber}
                              isMobile={isMobile}
                              onShowGroups={() => setIsGroupsDrawerOpen(true)}
                              groupsKey={groupsKey}
                            />
                          </Col>
                        </ConditionalRender>
                        <Col md={templateName ? 16 : 24}>
                          <ConditionalRender condition={isExcelMode}>
                            <ExcelUploadSection
                              templateName={templateName}
                              templateInputs={templateInputs}
                              uploadProps={uploadProps}
                              fileList={fileList}
                              disableUpload={disableUpload}
                              uploadError={uploadError}
                              importedData={importedData}
                              downloadSampleExcel={downloadSampleExcel}
                            />
                          </ConditionalRender>
                        </Col>
                      </Row>
                      {/* Handling form buttons */}
                      <Flex
                        className="mt-4"
                        justifyContent={isMobile ? undefined : "end"}
                        style={isMobile ? { width: '100%' } : undefined}
                        gap={10}
                      >
                        <ConditionalRender condition={isMobile}>
                          <Button
                            onClick={() => setIsPreviewDrawerOpen(true)}
                            loading={loading}
                            style={isMobile ? { flex: 1 } : undefined}
                          >
                            Preview
                          </Button>
                        </ConditionalRender>
                        <PermissionChecker permission="Create Campaign">
                          <Button
                            onClick={() => showScheduleModel()}
                            loading={loading}
                            style={isMobile ? { flex: 1 } : undefined}
                          >
                            Schedule
                          </Button>
                        </PermissionChecker>
                        <PermissionChecker permission="Create Campaign">
                          <Button
                            type="primary"
                            htmlType="submit"
                            onClick={(e) => HandleSubmitCampaign(e)}
                            loading={loading}
                            style={isMobile ? { flex: 1 } : undefined}
                          >
                            Submit
                          </Button>
                        </PermissionChecker>

                      </Flex>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Card>
          </Col>
          {/* mobile preview  */}
          <ConditionalRender condition={!isMobile}>
            <Col xs={24} sm={24} md={24} lg={6}>
              <Card title="Campaign Preview">
                <NewMobilePreview
                  requestType={requestType}
                  template={template}
                  mediaLink={mediaLink}
                  thumbnail={thumbnail}
                  newMesaage={newMesaage}
                  urlButtons={urlButtons}
                  customMessage={customMessage}
                />
              </Card>
            </Col>
          </ConditionalRender>
        </PermissionChecker>
        {/* Contact Groups: only visible in manual mode */}
        <PermissionChecker permission="View Group">
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={6}
            style={{ display: (isExcelMode || isMobile) ? 'none' : 'block' }}
          >
            <Groups
              key={groupsKey}
              ref={groupsRef}
              GetGroupNumber={GetGroupNumber}
            />
          </Col>
        </PermissionChecker>

        {/* Excel Guidelines: replaces Contact Groups in excel mode */}
        <ConditionalRender condition={isExcelMode}>
          <Col xs={24} sm={24} md={24} lg={6}>
            <Card title="Excel Campaign Guidelines">
              <List
                itemLayout="horizontal"
                dataSource={listData}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<RightOutlined style={{ color: "#00a5f4" }} />}
                      title={item}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </ConditionalRender>
      </Row>
      <ConditionalRender condition={isMobile}>
        <CustomDrawer
          title="Campaign Preview"
          open={isPreviewDrawerOpen}
          onClose={() => setIsPreviewDrawerOpen(false)}
          width={400}
        >
          <NewMobilePreview
            requestType={requestType}
            template={template}
            mediaLink={mediaLink}
            thumbnail={thumbnail}
            newMesaage={newMesaage}
            urlButtons={urlButtons}
            customMessage={customMessage}
          />
        </CustomDrawer>
      </ConditionalRender>
      <ConditionalRender condition={isMobile}>
        <CustomDrawer
          title="Contact Groups"
          open={isGroupsDrawerOpen}
          onClose={() => setIsGroupsDrawerOpen(false)}
          width={400}
          bodyStyle={{ padding: 0 }}
          styles={{ body: { padding: '0' } }}
          extra={
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              onChange={e => groupsRef.current?.onSearch(e.target.value)}
              size="small"
              style={{ width: 160 }}
            />
          }
          footer={
            groupSelection.groupsCount > 0 ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  background: "#fdfdfd",
                  borderTop: "1px solid #f0f0f0",
                  gap: "16px",
                }}
              >
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                  <div>
                    <span style={{ color: "#8c8c8c", marginRight: 4, fontSize: "12px" }}>Selected:</span>
                    <span style={{ fontWeight: "bold", color: "#1890ff", fontSize: "13px" }}>
                      {groupSelection.groupsCount} {groupSelection.groupsCount === 1 ? 'group' : 'groups'}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: "#8c8c8c", marginRight: 4, fontSize: "12px" }}>Total Contacts:</span>
                    <span style={{ fontWeight: "bold", color: "#52c41a", fontSize: "13px" }}>{groupSelection.contactsCount}</span>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <Button
                    type="primary"
                    onClick={() => setIsGroupsDrawerOpen(false)}
                    style={{ width: "100%" }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            ) : null
          }
        >
          <Groups
            key={groupsKey}
            ref={groupsRef}
            GetGroupNumber={GetGroupNumber}
            noCard={isMobile}
            hideSearch={true}
          />
        </CustomDrawer>
      </ConditionalRender>
    </>
  );
};

export default Campaign;
